import type { ThresholdStatus } from "@/lib/nutritionEngine";

const STATUS_COLOR: Record<ThresholdStatus, string> = {
  below: "#B97A5A", // clay-500
  "on-track": "#4A6F5E", // forest-500
  above: "#C98A3E", // amber-500
};

const STATUS_LABEL: Record<ThresholdStatus, string> = {
  below: "Below target",
  "on-track": "On track",
  above: "Above target",
};

export default function ProgressRing({
  label,
  value,
  target,
  unit,
  status,
  size = 128,
}: {
  label: string;
  value: number;
  target: number;
  unit: string;
  status: ThresholdStatus;
  size?: number;
}) {
  const stroke = size * 0.09;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(1, target > 0 ? value / target : 0);
  const offset = circumference * (1 - pct);
  const color = STATUS_COLOR[status];

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#E3DED3"
            strokeWidth={stroke}
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={stroke}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.5s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold text-ink">{Math.round(value)}</span>
          <span className="text-xs text-forest-500">
            / {Math.round(target)} {unit}
          </span>
        </div>
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-ink">{label}</p>
        <p className="text-xs" style={{ color }}>
          {STATUS_LABEL[status]}
        </p>
      </div>
    </div>
  );
}
